import React from "react";
import {
  FONT_FAMILIES,
  FONT_SIZE_MAX,
  FONT_SIZE_MIN,
  applyColumnsOrder,
  resolveWidgetOptions,
} from "./posterWidgetOptions";

function Section({ title, children }) {
  return (
    <div className="flex flex-col gap-1.5 border-l-2 border-neutral-400/40 pl-2">
      <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-600">{title}</span>
      <div className="flex flex-wrap items-center gap-1.5">{children}</div>
    </div>
  );
}

function NumberField({ label, value, min, max, onChange }) {
  return (
    <label className="flex items-center gap-1 text-xs text-neutral-700">
      <span className="font-semibold">{label}</span>
      <input
        type="number"
        className="w-16 rounded border border-neutral-400 bg-white px-1.5 py-0.5 text-xs"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(Math.max(min, Math.min(max, Math.round(n))));
        }}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="flex items-center gap-1 text-xs text-neutral-700">
      <span className="font-semibold">{label}</span>
      <select
        className="rounded border border-neutral-400 bg-white px-1.5 py-0.5 text-xs"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function CheckField({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-1 text-xs text-neutral-700">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5"
      />
      <span>{label}</span>
    </label>
  );
}

const ALIGN_OPTIONS = [
  { id: "left", label: "Izq" },
  { id: "center", label: "Cent" },
  { id: "right", label: "Der" },
];

const FONT_OPTIONS = FONT_FAMILIES.map((f) => ({ id: f.id, label: f.label }));

const BRACKET_LAYOUT_OPTIONS = [
  { id: "list", label: "Lista" },
  { id: "rounds", label: "Rondas (columnas)" },
  { id: "fields", label: "Por campo (horario)" },
];

/**
 * Compact column reorderer used inside the inspector. Renders a horizontal
 * list of chips for the columns currently available in the widget; each
 * chip has ← / → buttons to swap with the neighbour. We compute the final
 * order from the available list reordered by `currentOrder`, so a stale
 * id (e.g. a deleted group) silently disappears and a new one shows up
 * at the end without breaking the saved state.
 */
function ColumnsOrderControl({ available, currentOrder, onChange }) {
  if (!Array.isArray(available) || available.length < 2) return null;

  const ordered = applyColumnsOrder(available, currentOrder || [], (it) => it.id);

  const move = (idx, delta) => {
    const next = ordered.slice();
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next.map((it) => String(it.id)));
  };

  const reset = () => onChange([]);

  return (
    <Section title="Orden de columnas">
      <div className="flex flex-wrap items-center gap-1.5">
        {ordered.map((col, idx) => (
          <span
            key={col.id}
            className="inline-flex items-center gap-1 rounded-full border border-neutral-300 bg-white px-2 py-0.5 text-[11px] text-neutral-700"
            title={col.label}
          >
            <button
              type="button"
              className="rounded px-1 py-0.5 text-neutral-500 hover:bg-neutral-200 disabled:opacity-30"
              onClick={() => move(idx, -1)}
              disabled={idx === 0}
              aria-label={`Mover ${col.label} a la izquierda`}
            >
              ←
            </button>
            <span className="max-w-[10rem] truncate font-semibold">{col.label}</span>
            <button
              type="button"
              className="rounded px-1 py-0.5 text-neutral-500 hover:bg-neutral-200 disabled:opacity-30"
              onClick={() => move(idx, 1)}
              disabled={idx === ordered.length - 1}
              aria-label={`Mover ${col.label} a la derecha`}
            >
              →
            </button>
          </span>
        ))}
        <button
          type="button"
          className="rounded px-2 py-0.5 text-[11px] font-semibold text-neutral-600 hover:bg-neutral-200"
          onClick={reset}
          disabled={!Array.isArray(currentOrder) || currentOrder.length === 0}
        >
          Auto
        </button>
      </div>
    </Section>
  );
}

export default function WidgetOptionsInspector({ kind, options, onPatch, availableColumns }) {
  const opts = resolveWidgetOptions(kind, options);
  const patch = (partial) => onPatch({ ...opts, ...partial });
  const patchAlign = (key, value) => onPatch({ ...opts, align: { ...(opts.align || {}), [key]: value } });
  const patchColumns = (key, value) => onPatch({ ...opts, columns: { ...(opts.columns || {}), [key]: value } });

  return (
    <div className="flex flex-wrap items-center gap-3 rounded border border-neutral-300 bg-neutral-50 px-3 py-2">
      <Section title="Tipografía">
        <SelectField
          label="Fuente"
          value={opts.fontFamily}
          onChange={(v) => patch({ fontFamily: v })}
          options={FONT_OPTIONS}
        />
        <NumberField
          label="Tam"
          value={opts.fontSize}
          min={FONT_SIZE_MIN}
          max={FONT_SIZE_MAX}
          onChange={(v) => patch({ fontSize: v })}
        />
      </Section>

      {kind === "groups" ? (
        <>
          <Section title="Columnas">
            <CheckField
              label="Escudo"
              checked={opts.columns?.shield !== false}
              onChange={(v) => patchColumns("shield", v)}
            />
            <CheckField
              label="Pts"
              checked={opts.columns?.points !== false}
              onChange={(v) => patchColumns("points", v)}
            />
            <CheckField
              label="GF"
              checked={opts.columns?.goalsFor !== false}
              onChange={(v) => patchColumns("goalsFor", v)}
            />
            <CheckField
              label="GC"
              checked={opts.columns?.goalsAgainst !== false}
              onChange={(v) => patchColumns("goalsAgainst", v)}
            />
            <CheckField
              label="PJ"
              checked={opts.columns?.played !== false}
              onChange={(v) => patchColumns("played", v)}
            />
          </Section>
          <Section title="Alineación">
            <SelectField
              label="Equipo"
              value={opts.align?.team ?? "left"}
              onChange={(v) => patchAlign("team", v)}
              options={ALIGN_OPTIONS}
            />
            <SelectField
              label="Números"
              value={opts.align?.numbers ?? "center"}
              onChange={(v) => patchAlign("numbers", v)}
              options={ALIGN_OPTIONS}
            />
          </Section>
        </>
      ) : null}

      {kind === "matches" ? (
        <>
          <Section title="Visibilidad">
            <CheckField
              label="Campo"
              checked={opts.showField !== false}
              onChange={(v) => patch({ showField: v })}
            />
          </Section>
          <Section title="Alineación">
            <SelectField
              label="Equipos"
              value={opts.align?.teams ?? "left"}
              onChange={(v) => patchAlign("teams", v)}
              options={ALIGN_OPTIONS}
            />
            <SelectField
              label="Números"
              value={opts.align?.numbers ?? "center"}
              onChange={(v) => patchAlign("numbers", v)}
              options={ALIGN_OPTIONS}
            />
          </Section>
        </>
      ) : null}

      {kind === "schedule" ? (
        <>
          <Section title="Visibilidad">
            <CheckField
              label="Letra de grupo"
              checked={opts.showGroupLetter !== false}
              onChange={(v) => patch({ showGroupLetter: v })}
            />
            <CheckField
              label="Casilla de resultado"
              checked={!!opts.showResultBox}
              onChange={(v) => patch({ showResultBox: v })}
            />
          </Section>
          <Section title="Alineación">
            <SelectField
              label="Partidos"
              value={opts.align?.match ?? "left"}
              onChange={(v) => patchAlign("match", v)}
              options={ALIGN_OPTIONS}
            />
          </Section>
        </>
      ) : null}

      {kind === "bracket" ? (
        <>
          <Section title="Layout">
            <SelectField
              label="Disposición"
              value={opts.bracketLayout ?? "list"}
              onChange={(v) => patch({ bracketLayout: v })}
              options={BRACKET_LAYOUT_OPTIONS}
            />
          </Section>
          <Section title="Visibilidad">
            <CheckField
              label="Mostrar plazas (1º Grupo A…)"
              checked={opts.showFeederLabels !== false}
              onChange={(v) => patch({ showFeederLabels: v })}
            />
            <CheckField
              label="Escudo"
              checked={!!opts.showShield}
              onChange={(v) => patch({ showShield: v })}
            />
          </Section>
        </>
      ) : null}

      <ColumnsOrderControl
        available={availableColumns}
        currentOrder={opts.columnsOrder}
        onChange={(order) => patch({ columnsOrder: order })}
      />
    </div>
  );
}
